/* eslint-disable no-plusplus */
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { designActions, templateActions } from '../../actions';
import { Header, Dimmer, Button, Modal } from 'semantic-ui-react';
import InfiniteScroller from 'react-infinite-scroller';
import HorizontalCards from '../../components/HorizontalCards/HorizontalCards';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import SearchBox from './SearchBox';
import TemplatePreview from '../../components/TemplatePreview/TemplatePreview';
import './Startup.css';

// eslint-disable-next-line no-unused-vars
const Startup = ({ mobile }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(state => state.authentication.user);
  const hasSubscription = useSelector(state => state.subscription.hasSubscription);
  const userId = user.role === 'client' ? user.userId : null;
  const designs = useSelector(state => state.design.designs);
  const templates = useSelector(state => state.template.templates);
  const hasMoreTemplate = useSelector(state => state.template.hasMoreTemplate);
  const loading = useSelector(state => state.template.status);
  const [activeDim, setActiveDim] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const selectedTemplateRef = useRef();
  const initRef = useRef(true);
  const templateSkipRef = useRef(3);

  useEffect(() => {
    dispatch(designActions.search(userId, {}, true));
    dispatch(templateActions.search({ groupBy: 'categories', perGroupLimit: 15, skip: 0, limit: 3 }, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initRef.current) {
      initRef.current = false;
      return;
    }
    const mainElement = document.getElementsByClassName('main')[0];
    if (mainElement.clientHeight === mainElement.scrollHeight && hasMoreTemplate && loading !== 'loading') {
      dispatch(
        templateActions.search(
          { groupBy: 'categories', perGroupLimit: 15, skip: templateSkipRef.current, limit: 1 },
          false,
        ),
      );
      templateSkipRef.current++;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  const createDesign = template => {
    if (_.get(template, 'data.pages', []).length > 1) {
      selectedTemplateRef.current = template;
      setShowPreview(true);
    } else {
      // Block action if template has price and user hadn't subscription
      if (user.role === 'client' && _.get(template, 'data.price', 0) > 0 && !hasSubscription) {
        setActiveDim(true);
        return;
      }

      dispatch(
        designActions.create({
          templateId: _.get(template, 'data.id'),
          templateCategory: _.get(template, 'data.categories', [''])[0],
        }),
      );
    }
  };

  // eslint-disable-next-line react/prop-types
  const customInfiniteScroller = ({ hasMore, items }) => {
    const itemsToShow = [];
    // eslint-disable-next-line react/prop-types
    items.forEach((templateGroup, index) => {
      itemsToShow.push(
        <HorizontalCards
          key={index}
          parent="Startup"
          title={t(`tags:${templateGroup._id}`)}
          showMore={t('showMore')}
          itemType="templates"
          items={templateGroup.templates}
          className={_.camelCase(templateGroup._id)}
          height={200}
          onShowMoreClick={() => history.push(`/home/templates/categories/${templateGroup._id}`)}
          onItemSelect={template => createDesign(JSON.parse(template))}
        />,
      );
    });
    return (
      <InfiniteScroller
        pageStart={0}
        threshold={100}
        initialLoad={false}
        hasMore={hasMore}
        loadMore={() => {
          loading !== 'loading' &&
            dispatch(
              templateActions.search(
                {
                  groupBy: 'categories',
                  perGroupLimit: 15,
                  skip: templateSkipRef.current++,
                  limit: 1,
                },
                false,
              ),
            );
        }}
        useWindow={false}
        getScrollParent={() => document.getElementsByClassName('main')[0]}
      >
        {itemsToShow}
      </InfiniteScroller>
    );
  };

  return (
    <>
      <div className="startup startupTop">
        <div className="headerBanners">
          <div className="searchBanner">
            <div className="backImageBox"></div>
            <div className="backgroundGradient">
              <div className="searchBannerContent">
                <Header as="h1" content="یادگیری هر درس و مهارت، با یک طراحی ساده و دیدنی" />
                <span className="content">قالب‌ها و منابع آموزشی را بر اساس پایه، درس یا موضوع پیدا کنید و فعالیت دلخواهتان را بسازید.</span>
                <SearchBox placeholder="مثلاً «جمع»، «ساعت‌خوانی» یا «حیوانات» را جست‌وجو کنید" />
              </div>
            </div>
          </div>
          <div className="headerBanner">
            <div className="backImageBox"></div>
            <div className="backgroundGradient">
              <div className="headerBannerContent">
                <span className="title">دستیار هوشمند شخصی شما</span>
                <span className="content">برای ایده‌پردازی و آماده‌کردن محتوای آموزشی، از دستیار مندا کمک بگیرید.</span>
                <button type="button" className="btn" onClick={() => history.push('/home/assistant')}>
                  ورود به دستیار
                </button>
              </div>
            </div>
          </div>
        </div>
        <HorizontalCards
          parent="Startup"
          title={t('yourDesigns')}
          showMore={t('showMore')}
          itemType="designs"
          items={designs}
          height={200}
          onTitleClick={() => history.push('/home/design-list')}
          onShowMoreClick={() => history.push('/home/design-list')}
          onItemSelect={designId => history.push(`/edit/design/${designId}`)}
        />
      </div>
      <div className="startup">
        {customInfiniteScroller({ items: templates, hasMore: hasMoreTemplate })}
        <Dimmer active={activeDim} blurring="true" className="attentionAlert">
          <Header as="h2" icon>
            {t('noSubscription')}
            <br />
          </Header>
          <div className="dimContent">
            <Button className="vButton colorPrimary" content={t('gotIt')} onClick={() => setActiveDim(false)} />
          </div>
        </Dimmer>
      </div>

      <Modal size="large" open={showPreview} className="templatePreview" onClose={() => setShowPreview(false)}>
        <TemplatePreview template={selectedTemplateRef.current} />
      </Modal>
    </>
  );
};

Startup.propTypes = {
  mobile: PropTypes.any,
};

export default Startup;
